<template>
    <div :class="['autocomplete-wrapper', {'open': searchFocused}]">
        <SiteInput
            v-model="selectedItem.formatted_address"
            :class="{'focused': searchFocused}"
            name="selectitem"
            ref="inputBox"
            autocomplete="off"
            :disabled="disabled"
            :label="label ? label : t.label_location"
            @input="inputLimiting"
            @keydown="searchFocused = true"
            @keydown.up="positionUp"
            @keydown.down="positionDown"
            @keydown.enter.prevent="addItem"
            @keydown.esc="searchFocused = false"
            @click="searchFocused = true"
        />
        <ul
            v-if="searchFocused && (itemsList.length || showError)"
            id="autocomplete-options"
            class="dropdown-menu autocomplete"
        >
            <li v-if="showError">
                <div class="error">{{ t.get_location_failed }}</div>
            </li>
            <li v-else class="menu-item">
                <a
                    v-for="(item, index) in itemsList"
                    :key="index"
                    href="#"
                    :class="{'highlight': index === position}"
                    @click.prevent="selected(item)"
                >
                    {{ item.formatted_address }}
                </a>
            </li>
        </ul>
    </div>
</template>

<script>
import SiteInput from 'Site/component/SiteInput.vue';
import { throttle } from 'underscore';
import opencage from 'Location/js/opencage';

export default {
    name: 'GeocompleteSearch',
    components: {
        SiteInput,
    },
    props: {
        /**
         * Is input disabled?
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Field label
         */
        label: {
            type: String,
            default: '',
        },
        /**
         * Get raw address data in lookup?
         */
        raw: {
            type: Boolean,
            default: false,
        },
        /**
         * Translations object
         */
        t: {
            type: Object,
            required: true
        },
        /**
         * User input value
         */
        value: {
            type: String,
            required: false,
        }
    },
    data() {
        return {
            allItems: [],
            check: null,
            minSearchLength: 3,
            position: -1,
            searchFocused: false,
            selectedItem: {
                formatted_address: this.value
            },
            showError: false,
            siteLocale: this.$store.state.siteConfig.siteLocale,
            timeDelay: 1500,
            userInput: '',
        };
    },
    mounted() {
        this.$nextTick(() => {
            document.querySelector('html').addEventListener('click', (e) => {
                if (this.$el.contains(e.target)) {
                    return;
                }
                if (this.$el.parentElement) {
                    this.searchFocused = false;
                }
            });
        });
    },
    computed: {
        itemsList() {
            // Apply searchTerm filter
            // && filter out already selected items
            return this.allItems.filter(item => this.selectedItem.formatted_address !== item.formatted_address);
        },
    },
    methods: {
        addItem() {
            if ((this.position === -1 || !this.itemsList.length) && this.selectedItem.formatted_address) {
                const setSelectedViaSearch = this.allItems.filter(item => item.formatted_address === this.selectedItem.formatted_address);
                if (setSelectedViaSearch.length) {
                    this.selected(setSelectedViaSearch);
                }
            } else if (this.position !== -1) {
                this.selected(this.itemsList[this.position]);
            }
            this.position = -1;
            return false;
        },
        inputLimiting(input) {
            this.userInput = input;
            if (this.disabled) {
                this.allItems = [];
                return;
            }
            this.searchFocused = true;
            if (input.length >= this.minSearchLength) {
                if (!this.check) {
                    this.check = throttle(this.lookup, this.timeDelay);
                }
                this.check();
            }
        },
        /**
         * Lookup user location input.
         *
         */
        async lookup() {
            this.showError = false;
            if (this.userInput < this.minSearchLength) {
                return;
            }

            const locations = await opencage(this.userInput, { raw: this.raw }).catch(() => {
                this.showError = true;
            });

            if (locations.length) {
                this.allItems = locations;
            }
        },
        /**
         * Get and emit full details for user's chosen location
         *
         * @param {Object} item
         */
        async selected(item) {
            if (this.disabled) {
                this.allItems = [];
                this.searchFocused = false;
                return;
            }
            this.selectedItem = item;

            this.searchFocused = false;
            /**
             * Emit selected location details
             *
             * @param {Object} this.selectedItem
             */
            this.$emit('user-selected', this.selectedItem);
        },
        positionDown() {
            this.position = this.position < this.itemsList.length - 1 ? this.position + 1 : this.position;
        },
        positionUp() {
            this.position = this.position > -1 ? this.position - 1 : -1;
        },
    },
    watch: {
        position() {
            this.$nextTick(() => {
                if (this.searchFocused) {
                    document.getElementById('autocomplete-options').scrollTop = this.position >= 0 ? this.position * 23 : 0;
                }
            });
        },
        value() {
            this.selectedItem.formatted_address = this.value;
            this.showError = false;
        }
    },
};
</script>

<style lang="scss" scoped>
@import "~Site/scss/variables";

.autocomplete {
    max-height: 300px;
    width: 100%;
    overflow-y: scroll;
    border: 1px solid #ececec;
    list-style: none;
    padding: 0;
    position: absolute;
    z-index: 101;
    background-color: #fff;
    a {
        padding: 8px 10px 8px 8px;
        display: inline-block;
        width: 100%;
    }
    > a:nth-of-type(even) {
        background-color: #f5f5f5;
    }
    a.highlight, a:hover {
        background-color: $text-color;
        text-decoration: none;
        color: $navbar-inverse-link-color;
    }
    &.dropdown-menu {
        top: auto;
        > li > a {
            line-height: 1.8;
        }
    }
    .error {
        margin: 10px;
    }
}

.autocomplete-wrapper {
    position: relative;
    .form-group {
        margin: 0;
    }
}
</style>
